import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { Badge } from '@/components/ui/badge';

// Component to render the variable as a colored pill
const VariableComponent = ({ node }: any) => {
  const variable = node.attrs.variable;
  
  // Determine category and color based on variable prefix
  const getCategoryStyle = (variable: string) => {
    if (variable.includes('customer.') || variable.includes('contact.')) {
      return { variant: 'accent' as const, accent: 'blue-violet' as const };
    } else if (variable.includes('transaction.') || variable.includes('account.')) {
      return { variant: 'accent' as const, accent: 'teal' as const };
    } else if (variable.includes('company.') || variable.includes('credit_controller')) {
      return { variant: 'accent' as const, accent: 'purple' as const };
    } else if (variable.includes('payment.')) {
      return { variant: 'accent' as const, accent: 'orange' as const };
    }
    return { variant: 'secondary' as const, accent: undefined };
  };

  const style = getCategoryStyle(variable);

  return (
    <NodeViewWrapper as="span" className="inline-flex items-center">
      <Badge 
        variant={style.variant}
        accent={style.accent}
        className="text-[10px] font-mono cursor-default select-none mx-0.5"
        contentEditable={false}
      >
        {variable}
      </Badge>
    </NodeViewWrapper>
  );
};

// TipTap extension for variables
export const VariableExtension = Node.create({
  name: 'variable',

  group: 'inline',

  inline: true,

  atom: true,

  addAttributes() {
    return {
      variable: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable]',
        getAttrs: (element: HTMLElement | string) => {
          if (typeof element === 'string') return false;
          const variable = element.getAttribute('data-variable');
          return variable ? { variable } : false;
        },
      },
    ];
  },

  renderHTML({ node }: any) {
    // Return empty span with just the data attribute - no content
    return ['span', { 
      class: 'variable-node',
      'data-variable': node.attrs.variable
    }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableComponent);
  },

  addCommands() {
    return {
      insertVariable: (variable: string) => ({ commands }: any) => {
        return commands.insertContent({
          type: this.name,
          attrs: { variable },
        });
      },
    };
  },
});
